import React, { useState } from 'react';
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { submitEnquiry, ApiError } from "../services/api";
import usePageTracking from "../hooks/usePageTracking";
import { trackEvent } from '../services/mixpanel'; // ✅ ADD THIS
import { usePhoneNumber } from "../hooks/usePhoneNumber";

const GetAQuoteEnquiryForm = ({title}) => {
  const pageInfo = usePageTracking();
  const phoneNumber = usePhoneNumber();

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    interest: "buying",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const isFormValid =
    formData.name.trim().length > 0 && 
    formData.phone.trim().length > 0 && 
    formData.email.trim().length > 0;

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (formData.phone.length < 10) {
      newErrors.phone = "Please enter a valid phone number";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    return newErrors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // ✅ ADD THIS - Track form opening
  React.useEffect(() => {
    trackEvent('Get a Quote Form Opened', {
      form_type: 'GetAQuoteEnquiryForm',
      timestamp: new Date().toISOString()
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = validateForm();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      
      // ✅ ADD THIS - Track validation errors
      trackEvent('Get a Quote Form Validation Failed', {
        fields_with_errors: Object.keys(newErrors),
        error_count: Object.keys(newErrors).length,
        form_type: 'GetAQuoteEnquiryForm'
      });
      
      return;
    }

    setIsSubmitting(true);

    try {
      const submissionData = {
        ...formData,
        source_page: {
          url: pageInfo.url,
          pathname: pageInfo.pathname,
          search: pageInfo.search,
          title: pageInfo.title,
          referrer: pageInfo.referrer,
          timestamp: new Date().toISOString()
        }
      };

      await submitEnquiry(submissionData);

      // ✅ ADD THIS - Track successful submission
      trackEvent('Get a Quote Form Submitted', {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        interest: formData.interest,
        form_type: 'GetAQuoteEnquiryForm'
      });

      setFormData({
        name: "",
        phone: "",
        email: "",
        interest: "buying",
      });

      setShowSuccessMessage(true);
    } catch (error) {
      console.error("Submission error:", error);

      // ✅ ADD THIS - Track submission errors
      trackEvent('Get a Quote Form Submission Failed', {
        name: formData.name,
        error_message: error?.message || 'Unknown error',
        form_type: 'GetAQuoteEnquiryForm'
      });
      
      let errorMessage = "Something went wrong. Please try again.";
      if (error instanceof ApiError) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsAppClick = () => {
    // ✅ ADD THIS - Track WhatsApp click
    trackEvent('Get a Quote WhatsApp Clicked', {
      form_type: 'GetAQuoteEnquiryForm',
      has_name: !!formData.name,
      has_email: !!formData.email,
      has_phone: !!formData.phone
    });

    const whatsappNumber = phoneNumber.replace(/\D/g, ""); // Remove all non-digit characters
    const message = encodeURIComponent("Hi, I'm interested in getting a quote.");
    window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');
  };

  return (
    <>
      <section className="enquiry-section">
        {title}
        <div className="form-container">
          {!showSuccessMessage ? (
            <>
              <div className="features-list">
                <div className="feature-item">
                  <img src="/images/getAQuote/handStars.png" alt="" className='w-[24px] h-[24px]' />
                  <span className="feature-text">Professional advice</span>
                </div>
                <div className="feature-item">
                  <img src="/images/getAQuote/clock.png" alt="" className='w-[24px] h-[24px]' />
                  <span className="feature-text">Quick responses</span>
                </div>
                <div className="feature-item">
                  <img src="/images/getAQuote/tagPrice.png" alt="" className='w-[24px] h-[24px]' />
                  <span className="feature-text">Best prices</span>
                </div>
                <div className="feature-item">
                  <img src="/images/getAQuote/verifiedCheck.png" alt="" className='w-[24px] h-[24px]' />
                  <span className="feature-text">All in one place.</span>
                </div>
              </div>
              <p className="cta-text">
                <span className="bold">Reach out today</span> and let Golden Sparrow help you <span className="bold">lift your business</span> to the next level.
              </p>
              <form onSubmit={handleSubmit} noValidate>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="name">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input 
                      type="text" 
                      id="name" 
                      name="name" 
                      placeholder="Name" 
                      value={formData.name}
                      onChange={handleInputChange}
                      className={errors.name ? "error" : ""}
                      autoComplete="off"
                    />
                    {errors.name && (
                      <p className="error-message">{errors.name}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input 
                      type="email" 
                      id="email" 
                      name="email" 
                      placeholder="Email" 
                      value={formData.email}
                      onChange={handleInputChange}
                      className={errors.email ? "error" : ""}
                      autoComplete="off"
                    />
                    {errors.email && (
                      <p className="error-message">{errors.email}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">
                      Phone number <span className="text-red-500">*</span>
                    </label>
                    <PhoneInput
                      country={"ae"}
                      value={formData.phone}
                      onChange={(phone) =>
                        setFormData((prev) => ({ ...prev, phone }))
                      }
                      onFocus={() => {
                        if (errors.phone) {
                          setErrors((prev) => ({ ...prev, phone: "" }));
                        }
                      }}
                      inputStyle={{
                        width: "100%",
                        height: "50px",
                        fontSize: "15px",
                        borderColor: errors.phone ? "#ef4444" : "#E0E0E0",
                        backgroundColor: errors.phone ? "#fef2f2" : "#ffffff",
                        borderRadius: '6px'
                      }}
                      buttonStyle={{
                        borderColor: errors.phone ? "#ef4444" : "#E0E0E0",
                        backgroundColor: "#ffffff",
                        borderRadius: '6px 0 0 6px'
                      }}
                      dropdownStyle={{
                        maxHeight: "200px",
                      }}
                      searchStyle={{
                        padding: "8px",
                        fontSize: "14px",
                      }}
                      enableSearch={true}
                      searchPlaceholder="Search countries"
                      placeholder="Enter phone number"
                      specialLabel=""
                    />
                    {errors.phone && (
                      <p className="error-message">{errors.phone}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Looking for</label>
                    <div className="radio-group-wrapper">
                      <span className="radio-label">I am interested in:</span>
                      <div className="radio-options">
                        <div className="radio-option">
                          <input 
                            type="radio" 
                            id="buying" 
                            name="interest" 
                            value="buying" 
                            checked={formData.interest === "buying"}
                            onChange={handleInputChange}
                          />
                          <label htmlFor="buying">Buying</label>
                        </div>
                        <div className="radio-option">
                          <input 
                            type="radio" 
                            id="renting" 
                            name="interest" 
                            value="renting" 
                            checked={formData.interest === "renting"}
                            onChange={handleInputChange}
                          />
                          <label htmlFor="renting">Renting</label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="button-group">
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={isSubmitting || !isFormValid}
                  >
                    {isSubmitting ? "Submitting..." : "Get a Quote"}
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-whatsapp"
                    onClick={handleWhatsAppClick}
                  >
                    <svg className="whatsapp-icon w-[24px] h-[24px]" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    Connect on WhatsApp
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div className="submit-message">
              <div className="tick-animation">
                <img
                  src="/images/tick_animation.gif"
                  alt="Success"
                  className="success-gif"
                />
              </div>
              <div className="success-content">
                <p className="success-title">Query submitted</p>
                <p className="success-message">Wallah! You're taking a worthy step towards your Business!</p>
                <h6 className="success-subtitle">Our expert will get to you <strong>soon!</strong></h6>
              </div>
            </div>
          )}
        </div>
      </section>
      <style jsx>{`
        .enquiry-section {
            max-width: 1280px;
            margin: clamp(2rem, 5vw, 5rem) auto;
            padding: 0 clamp(20px, 4vw, 32px);
        }
        .section-title {
            font-size: clamp(32px, 5vw, 57px);
            font-weight: 400;
            margin-bottom: 40px;
            letter-spacing:0;
            line-height:clamp(39px, 5vw, 64px);
        }

        .section-title .bold {
            font-weight: 700;
        }

        .form-container {
            border: 2px solid;
            border-image-source: linear-gradient(159.21deg, #FFD200 11.19%, #F7971E 101.23%);
            border-image-slice: 1;
            padding: clamp(30px, 5vw, 80px) clamp(30px, 5vw, 120px);;
            background-color: #ffffff;
            box-shadow: 0px 0px 44px 0px rgba(0, 0, 0, 0.2);
        }

        .features-list {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 20px;
            margin-bottom: 40px;
        }

        .feature-item {
            display: flex;
            align-items: center;
            justify-content:center;
            gap: 12px;
        }

        .feature-text {
            font-size: 20px;
            color: rgba(26, 26, 26, 1);
            font-weight: 500;
            line-height:32px;
            letter-spacing:0;
        }

        .cta-text {
            font-size: clamp(16px, 2.5vw, 22px);
            margin-bottom: 40px;
            text-align: center;
            color: rgba(26, 26, 26, 1);
            font-weight:500;
        }

        .cta-text .bold {
            font-weight: 700;
        }

        .form-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 24px;
            margin-bottom: 24px;
        }

        .form-group {
            display: flex;
            flex-direction: column;
        }

        .form-group.full-width {
            grid-column: 1 / -1;
        }

        label {
            font-size: 14px;
            line-height:20px;
            font-weight: 600;
            margin-bottom: 8px;
            color: rgba(68, 69, 72, 1);
        }

        input[type="text"],
        input[type="email"],
        input[type="tel"] {
            padding: 14px 16px;
            border: 1px solid rgba(91, 91, 91, 0.2);
            border-radius: 4px;
            font-size: 14px;
            transition: border-color 0.3s ease;
            background-color: #ffffff;
            color: rgba(0, 0, 0, 1);
            box-shadow: 0px 1px 2px 0px rgba(184, 200, 224, 0.2221);
        }

        input[type="text"]:focus,
        input[type="email"]:focus,
        input[type="tel"]:focus {
            outline: none;
            border-color: #FFD200;
        }

        input[type="text"].error,
        input[type="email"].error {
            border-color: #ef4444;
            background-color: #fef2f2;
        }

        input::placeholder {
            color: #999999;
        }

        .error-message {
            color: #ef4444;
            font-size: 13px;
            margin-top: 4px;
        }

        .text-red-500 {
            color: #ef4444;
        }

        .radio-group-wrapper {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 30px;
            background: rgba(245, 241, 230, 1);
            box-shadow: 0px 1px 2px 0px rgba(184, 200, 224, 0.2221);
            padding: 12px 0;
            border-radius: 4px;
        }

        .radio-label {
            font-size: 17px;
            font-weight: 600;
            color: rgba(0, 124, 145, 1);
            display: block;
            text-align: center;
        }

        .radio-options {
            display: flex;
            gap: 40px;
            justify-content: center;
        }

        .radio-option {
            display: flex;
            align-items: center;
            gap: 10px;
            cursor: pointer;
        }

        .radio-option input[type="radio"] {
            width: 20px;
            height: 20px;
            cursor: pointer;
            accent-color: #FFD200;
        }

        .radio-option label {
            color: rgba(0, 0, 0, 1);
            font-size: 17px;
            line-height:24px;
            margin: 0;
            cursor: pointer;
            font-weight: 400;
        }

        .button-group {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 16px;
            margin-top: 30px;
        }

        .btn {
            padding: 16px 32px;
            border: none;
            border-radius: 6px;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
        }

        .btn:disabled {
            background: #c2c2c2;
            cursor: not-allowed;
            opacity: 0.6;
        }

        .btn-primary {
            background: linear-gradient(159.21deg, #FFD200 11.19%, #F7971E 101.23%);
            color: #000000;
            box-shadow: 0px 1px 6px 0px rgba(0, 0, 0, 0.22);
        }

        .btn-primary:hover:not(:disabled) {
            background: linear-gradient(159.21deg, #F7971E 11.19%, #FFD200 101.23%);
            transform: translateY(-2px);
            box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.22);
        }

        .btn-whatsapp {
            background: rgba(234, 254, 238, 1);
            color: rgba(19, 175, 47, 1);
            border: 3px solid rgba(19, 175, 47, 1);
        }

        .btn-whatsapp:hover {
        background: rgba(19, 175, 47, 1);
        border: 3px solid rgba(19, 175, 47, 1);
        box-shadow: 0px 1px 12px 0px rgba(0, 0, 0, 0.3);
        color: #ffffff;
        transform: translateY(-2px);
        }
        
        .btn-whatsapp:active{
            background: rgba(19, 175, 47, 1);
            border: 3px solid rgba(0, 160, 28, 1);
            box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.5) inset;
        }

        .submit-message {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            text-align: center;
            padding: 60px 20px;
            min-height: 400px;
        }

        .tick-animation {
            margin-bottom: 20px;
        }

        .success-gif {
            width: 120px;
            height: 120px;
        }

        .success-content {
            max-width: 600px;
            margin: 0 auto;
        }

        .success-title {
            font-size: 20px;
            font-weight: 600;
            margin-bottom: 16px;
            color: #333333;
        }

        .success-message {
            font-size: 28px;
            font-weight: 600;
            margin-bottom: 16px;
            color: #000000;
            line-height: 1.4;
        }

        .success-subtitle {
            font-size: 16px;
            color: #666666;
            font-weight: 400;
        }

        @media (max-width: 768px) {
            .form-container {
                padding: 30px 20px;
            }

            .features-list {
                grid-template-columns: 1fr;
                gap: 16px;
            }

            .feature-item {
            display: flex;
            align-items: center;
            justify-content:flex-start;
            gap: 12px;
        }

            .form-grid {
                grid-template-columns: 1fr;
                gap: 20px;
            }

            .button-group {
                grid-template-columns: 1fr;
            }

            .radio-options {
                gap: 30px;
            }

            .radio-group-wrapper {
                flex-direction: column;
                gap: 15px;
                padding: 16px 12px;
            }

            .success-gif {
                width: 100px;
                height: 100px;
            }

            .success-message {
                font-size: 22px;
            }
        }

        @media (max-width: 480px) {
            .section-title {
                font-size: 32px;
            }

            .form-container {
                padding: 24px 16px;
                border-width: 2px;
            }

            .cta-text {
                font-size: 15px;
            }

            .feature-text {
                font-size: 16px;
                line-height:28px;
            }

            .success-gif {
                width: 80px;
                height: 80px;
            }

            .success-title {
                font-size: 18px;
            }

            .success-message {
                font-size: 20px;
            }

            .success-subtitle {
                font-size: 14px;
            }

            .btn{
            padding: 12px 16px;
            
            }
        }
      `}</style>
    </>
  );
};

export default GetAQuoteEnquiryForm;